import { SelectRef } from '@/components/Select/Select';
import { useConfigContext } from '@/context/configContext';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { CurrencyInput, Tooltip, Typography } from '@analytical-alley/ui';
import { Label } from 'flowbite-react';
import { useRef } from 'react';

const MediaKPIInput = ({
    estimatedBudget,
    setEstimatedBudget,
    label,
    disabled = false,
    limits,
}: {
    estimatedBudget: number | undefined;
    setEstimatedBudget: (value: number | undefined) => void;
    label?: string;
    disabled?: boolean;
    limits: { min: number; max: number } | null;
}) => {
    const selectRef = useRef<SelectRef>(null);
    const { locale } = useConfigContext();
    const {
        contributionFormatter,
        isFinancialKPI,
        project: { contributionFormatting },
    } = useDashboardGlobalContext();
    const value = estimatedBudget == null ? '' : Math.round(estimatedBudget);

    return (
        <div>
            <div className="mb-1 block">
                <Label
                    onClick={() => {
                        selectRef?.current?.click?.();
                    }}
                    className="font-sans font-bold text-xs uppercase tracking-wider text-white/50 mb-1"
                    htmlFor="budget"
                >
                    {label || 'Enter Value to Optimize'}
                </Label>
            </div>
            <Tooltip placement="bottom">
                <Tooltip.Trigger asChild>
                    <CurrencyInput
                        id="budget"
                        className="w-full self-center text-nowrap justify-center px-4 py-2 text-sm leading-5 rounded-lg shadow-lg backdrop-blur-[15px] bg-white/10 glass text-dark dark:text-white border-0 focus-visible:outline-hidden dark:shadow-sm-light"
                        intlConfig={
                            isFinancialKPI
                                ? {
                                      locale,
                                      currency:
                                          contributionFormatting.toUpperCase(),
                                  }
                                : undefined
                        }
                        suffix={
                            !isFinancialKPI && contributionFormatting === 'pcs'
                                ? ' PCS'
                                : undefined
                        }
                        disabled={disabled}
                        decimalScale={0}
                        allowNegativeValue={false}
                        value={value}
                        groupSeparator={' '}
                        onValueChange={(_, _1, values) => {
                            const value = values?.float;

                            if (value == null || value < 0) {
                                setEstimatedBudget(0);
                                return;
                            }

                            setEstimatedBudget(value);
                        }}
                    />
                </Tooltip.Trigger>
                {limits && (
                    <Tooltip.Content className="tooltip-content">
                        <table className="font-sans">
                            <tbody>
                                <tr>
                                    <td className="pr-2">
                                        <Typography className="text-black dark:text-black">
                                            Selected Media KPI:
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography className="text-dark dark:text-dark">
                                            {estimatedBudget
                                                ? contributionFormatter(
                                                      estimatedBudget,
                                                  )
                                                : '-'}
                                        </Typography>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="pr-2">
                                        <Typography className="text-black dark:text-black">
                                            Minimum Recommended Value:
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography className="text-dark dark:text-dark">
                                            {contributionFormatter(
                                                limits.min,
                                            )}
                                        </Typography>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="pr-2">
                                        <Typography className="text-black dark:text-black">
                                            Maximum Recommended Value:
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography className="text-dark dark:text-dark">
                                            {contributionFormatter(
                                                limits.max,
                                            )}
                                        </Typography>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Tooltip.Content>
                )}
            </Tooltip>
        </div>
    );
};

export default MediaKPIInput;
